import { Endpoints } from "services/api";
import { getFetch, setupUser } from "@wexinc/wex-common-web";
import { Idp } from "services/security";
import {
  AddMemberResponse,
  BaseOrganizationInfo,
  OrganizationDetails,
  OrganizationInfo,
  OrganizationInfoExpanded,
  OrganizationInvitation,
  OrganizationLineage,
  OrganizationLoginConfig,
  OrganizationMember,
  ParentOrganizationInfo,
} from "./models";

const apiRoot = "organizations";
const { doDelete, get, put, post } = getFetch(Endpoints.apiRoot);

async function getOrganizationDetails(id: string): Promise<OrganizationDetails> {
  const result = (await get({
    relativeRoute: `${apiRoot}/${id}`,
  })) as OrganizationEndpointModel;

  return convertOrganizationFromApiToOrganizationDetails(result);
}

function convertOrganizationFromApiToOrganizationDetails(input: OrganizationEndpointModel): OrganizationDetails {
  return {
    id: input.id,
    name: input.name,
    description: input.description,
    accessType: input.accessType,
    contactEmail: input.contactEmail || null,
    parent: input.parent || undefined,
    members: input.membersPage.content.map((i) => ({
      roles: i.roles,
      user: setupUser(i.user),
    })),
    invitees: input.invitesPage?.content.map((i) => setupInvitation(i)),
  };
}

async function saveOrganization(
  isNew: boolean,
  organization: OrganizationInfo | BaseOrganizationInfo,
): Promise<OrganizationDetails | OrganizationInfo> {
  if (isNew) {
    const result = (await post({
      relativeRoute: apiRoot,
      body: organization,
    })) as OrganizationEndpointModel;
    return convertOrganizationFromApiToOrganizationDetails(result);
  } else {
    return put({
      relativeRoute: apiRoot,
      body: organization,
    });
  }
}

function deleteOrganization(organizationId: string): Promise<Response> {
  return doDelete({
    relativeRoute: `${apiRoot}/${organizationId}`,
  });
}

function deleteOrganizationMember(organizationId: string, userId: string): Promise<Response> {
  return doDelete({
    relativeRoute: `${apiRoot}/${organizationId}/members/${userId}`,
  });
}

async function addOrganizationMembers(
  organizationId: string,
  emails: string[],
): Promise<AddMemberResponse<OrganizationInvitation>[]> {
  const body = emails.map((i) => ({ email: i }));
  const result = (await post({
    relativeRoute: `${apiRoot}/${organizationId}/members/batch`,
    body,
  })) as AddMemberResponse<OrganizationInvitationFromApi>[];

  return result.map(
    (i) =>
      ({
        email: i.email,
        error: i.error,
        invitation: i.invitation ? setupInvitation(i.invitation) : undefined,
        member: i.member
          ? (() => {
              setupUser(i.member.user);
              return i.member;
            })()
          : undefined,
        status: i.status,
      }) as AddMemberResponse<OrganizationInvitation>,
  );
}

function updateOrganizationMemberRoles(organizationId: string, organizationMember: OrganizationMember): Promise<void> {
  return put({
    relativeRoute: `${apiRoot}/${organizationId}/members/${organizationMember.user.id}/roles`,
    body: organizationMember.roles.map((i) => i.id),
  });
}

function revokeInvitation(organizationId: string, invitationId: number) {
  return doDelete({
    relativeRoute: `${apiRoot}/${organizationId}/invitations/${invitationId}`,
  });
}

function getAllIdps(): Promise<Idp[]> {
  return get({
    relativeRoute: "idps/all",
  });
}

function getOrganizationLoginConfiguration(id: string): Promise<OrganizationLoginConfig> {
  return get({
    relativeRoute: `${apiRoot}/${id}/login-config`,
  });
}

function getOrganizationHierarchy(id: string): Promise<OrganizationLineage> {
  return get({
    relativeRoute: `${apiRoot}/${id}/children`,
  });
}

function getTopLevelOrganizations(): Promise<OrganizationInfoExpanded[]> {
  return get({
    relativeRoute: `${apiRoot}/top-level/all`,
  });
}

export {
  addOrganizationMembers,
  deleteOrganization,
  deleteOrganizationMember,
  getOrganizationDetails,
  saveOrganization,
  updateOrganizationMemberRoles,
  revokeInvitation,
  getAllIdps,
  getOrganizationLoginConfiguration,
  getOrganizationHierarchy,
  getTopLevelOrganizations,
};

/* Models of the raw API data for internal-use here only */
export function setupInvitation(invite: OrganizationInvitationFromApi): OrganizationInvitation {
  const result: Partial<OrganizationInvitation> = {
    id: invite.id,
    email: invite.email,
  };
  result.createdAt = new Date(invite.createdAt);
  result.expiresAt = new Date(invite.expiresAt);
  return result as OrganizationInvitation;
}

export interface OrganizationEndpointModel extends OrganizationFromApiBase {
  parent: ParentOrganizationInfo | null;
  membersPage: PageInfo<OrganizationMember>;
  invitesPage: PageInfo<OrganizationInvitationFromApi>;
}

export interface OrganizationInvitationFromApi {
  id: number;
  email: string;
  createdAt: string;
  expiresAt: string;
}

export interface OrganizationFromApiBase {
  id: string;
  name: string;
  description: string;
  accessType: "EXTERNAL" | "INTERNAL";
  contactEmail: string | null;
}

interface PageInfo<T> {
  first?: boolean;
  last?: boolean;
  number?: 0;
  numberOfElements?: 0;
  size?: 0;
  totalElements?: 0;
  totalPages?: 0;
  content: T[];
}
